import { lazy, StrictMode, Suspense, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import './styles.css';
import { AuthProvider, useAuth } from './auth';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { Requests, RequestDetail } from './pages/Requests';
import { NewRequest } from './pages/NewRequest';
import { AssetDetail } from './pages/AssetDetail';
import { Approvals, Calendar, Studio } from './pages/Content';
import { AgentDetail, Agents, Cockpit, Portfolio, Radar } from './pages/Strategy';
import { Assistant } from './pages/Assistant';
import { Prospecting } from './pages/Prospecting';
import { SiteChat } from './pages/SiteChat';
import { SiteChatAdmin } from './pages/SiteChatAdmin';
import { Abm, Admin, Analytics, Campaigns, NotActive } from './pages/Operations';
import { Wall } from './pages/Wall';
import { ChangePassword } from './pages/ChangePassword';

// Agents Live 3D: carregado sob demanda (three.js só baixa quando a tela é aberta).
const AgentsLive3DPage = lazy(() => import('./features/agents-live-3d/AgentsLive3DPage'));
const AgentsTVMode = lazy(() => import('./features/agents-live-3d/AgentsTVMode'));
const Lazy3D = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center bg-[#020c24] text-cyan-100">Carregando Agents Live 3D…</div>}>{children}</Suspense>
);

function Protected({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  if (loading) return <div className="p-6"><Loading /></div>;
  if (!me) return <Navigate to="/login" replace />;
  if (me.user.mustChangePassword) return <ChangePassword />;
  return <>{children}</>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/painel" element={<Wall />} />
          <Route path="/atendimento" element={<SiteChat />} />
          <Route path="/painel-3d" element={<Lazy3D><AgentsTVMode /></Lazy3D>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route index element={<Home />} />
            <Route path="assistente" element={<Assistant />} />
            <Route path="prospeccao" element={<Prospecting />} />
            <Route path="atendimento-site" element={<SiteChatAdmin />} />
            <Route path="cockpit" element={<Cockpit />} />
            <Route path="demandas" element={<Requests />} />
            <Route path="demandas/nova" element={<NewRequest />} />
            <Route path="demandas/:id" element={<RequestDetail />} />
            <Route path="conteudo/:id" element={<AssetDetail />} />
            <Route path="aprovacoes" element={<Approvals />} />
            <Route path="calendario" element={<Calendar />} />
            <Route path="studio" element={<Studio />} />
            <Route path="agentes" element={<Agents />} />
            <Route path="agentes/:id" element={<AgentDetail />} />
            <Route path="agentes-3d" element={<Lazy3D><AgentsLive3DPage /></Lazy3D>} />
            <Route path="radar" element={<Radar />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="campanhas" element={<Campaigns />} />
            <Route path="eventos" element={<NotActive title="Eventos" phase="Fase 3" agents="A16" what="Planejamento de eventos, RSVP, kits e follow-up 30/60/90 (W09)." />} />
            <Route path="endomarketing" element={<NotActive title="Endomarketing AGIR" phase="Fase 3" agents="A17" what="Calendário interno, peças AGIR e feedback agregado (W10)." />} />
            <Route path="abm" element={<Abm />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="admin" element={<Admin />} />
            <Route path="*" element={<p>Página não encontrada.</p>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </StrictMode>,
);
